// src/pages/Settings/SecuritySettings.js
import React, { useState, useContext, useEffect, useRef } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { WalletContext } from '../../contexts/WalletContext';
import PasswordModal from '../../components/Auth/PasswordModal';
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import './SecuritySettings.css';
import { FiAlertTriangle, FiEye, FiEyeOff, FiCopy, FiCheck, FiKey } from 'react-icons/fi';
// Import secure memory utilities
import { maskSensitiveText, createTemporaryAccess } from '../../utils/secureMemoryUtils';

const SecuritySettings = () => {
  // Add translation hook
  const { t } = useTranslation();
  
  const { 
    hasPassword, 
    hasAutoPassword,
    isAuthenticated, 
    sessionTimeout,
    updateSessionTimeout,
    passwordDeferred,
    logout,
    resetWalletApp,
    verifyPassword,
    passwordHash,
    passwordSalt,
    removePassword
  } = useContext(AuthContext);
  
  const {
    getSeedPhrase,
    retryWalletDecryption,
    forceLoadSeedPhrase,
    hasSeedPhrase
  } = useContext(WalletContext);
  
  // Add local state to track password status and force re-renders
  const [localHasPassword, setLocalHasPassword] = useState(hasPassword);
  const [localPasswordDeferred, setLocalPasswordDeferred] = useState(passwordDeferred);
  const [refreshKey, setRefreshKey] = useState(0);
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordMode, setPasswordMode] = useState('create');
  const [timeoutMinutes, setTimeoutMinutes] = useState(sessionTimeout / (60 * 1000));
  const [isResetting, setIsResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  
  // Seed phrase visibility and verification state
  const [showSeedPhraseSection, setShowSeedPhraseSection] = useState(false);
  
  // SECURITY IMPROVEMENT: Don't store the actual seed phrase in state,
  // instead use a reference to a secure temporary accessor
  const [seedPhraseAccessor, setSeedPhraseAccessor] = useState(null);
  const [seedPhraseMasked, setSeedPhraseMasked] = useState('');
  
  const [showSeedPhrase, setShowSeedPhrase] = useState(false);
  const [seedCopied, setSeedCopied] = useState(false);
  const [seedPasswordVerified, setSeedPasswordVerified] = useState(false);
  const [seedPassword, setSeedPassword] = useState('');
  const [seedPasswordError, setSeedPasswordError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [seedPhraseTimeout, setSeedPhraseTimeout] = useState(null);
  
  // Cleanup function to securely clear sensitive data
  const clearSensitiveData = () => {
    if (seedPhraseAccessor && seedPhraseAccessor.destroy) {
      seedPhraseAccessor.destroy();
      setSeedPhraseAccessor(null);
    }
    
    if (seedPassword) {
      // Overwrite in memory before clearing
      setSeedPassword('0'.repeat(seedPassword.length));
      setSeedPassword('');
    }
    
    // Clear timeout to avoid memory leaks
    if (seedPhraseTimeout) {
      clearTimeout(seedPhraseTimeout);
      setSeedPhraseTimeout(null);
    }
  };
  
  // Clean up sensitive data when component unmounts
  useEffect(() => {
    return () => {
      clearSensitiveData();
    };
  }, []);
  
  // Set up auto-expiry timer when seed phrase is shown
  useEffect(() => {
    // Auto-hide seed phrase after 2 minutes of visibility
    if (showSeedPhrase && seedPasswordVerified) {
      const timer = setTimeout(() => {
        // Hide seed phrase automatically
        setShowSeedPhrase(false);
      }, 2 * 60 * 1000); // 2 minutes
      
      setSeedPhraseTimeout(timer);
      
      return () => {
        clearTimeout(timer);
        setSeedPhraseTimeout(null);
      };
    }
  }, [showSeedPhrase, seedPasswordVerified, t]);
  
  // Sync local state with context state
  useEffect(() => {
    setLocalHasPassword(hasPassword);
    setLocalPasswordDeferred(passwordDeferred);
    
    console.log('Security Settings Auth State (Updated):', { 
      hasPassword, 
      passwordDeferred, 
      localHasPassword,
      localPasswordDeferred,
      hasPasswordHash: !!passwordHash, 
      hasPasswordSalt: !!passwordSalt,
      isAuthenticated,
      refreshKey
    });
  }, [hasPassword, passwordDeferred, passwordHash, passwordSalt, isAuthenticated, refreshKey]);
  
  // Force a refresh when component mounts
  useEffect(() => {
    // Force a refresh after component mounts
    const timer = setTimeout(() => {
      setRefreshKey(prev => prev + 1);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Timeout options in minutes
  const timeoutOptions = [
    { value: 3, label: `3 ${t('settings.transaction.minutes')}` },
    { value: 5, label: `5 ${t('settings.transaction.minutes')}` },
    { value: 15, label: `15 ${t('settings.transaction.minutes')}` },
    { value: 30, label: `30 ${t('settings.transaction.minutes')}` },
    { value: 60, label: `1 ${t('time.hour')}` }
  ];
  
  // Handle password modal
  const handlePasswordClick = (mode) => {
    setPasswordMode(mode);
    setShowPasswordModal(true);
  };
  
  // Handle password modal success - with enhanced refresh
  const handlePasswordSuccess = () => {
    setShowPasswordModal(false);
    
    // Force component refresh after password operation
    setTimeout(() => {
      console.log("Forcing component refresh after password operation");
      setRefreshKey(prev => prev + 1);
    }, 100);
  };
  
  // Handle password modal cancel
  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
  };
  
  // Handle session timeout change
  const handleTimeoutChange = (e) => {
    const minutes = parseInt(e.target.value, 10);
    setTimeoutMinutes(minutes);
    updateSessionTimeout(minutes);
  };
  
  // Show reset confirmation input
  const handleShowResetConfirm = () => {
    setShowResetConfirm(true);
    setResetConfirmText('');
  };
  
// Handle wallet reset
const handleResetWallet = () => {
  // Validate that user typed "confirm"
  if (resetConfirmText.toLowerCase() !== 'confirm') {
    return;
  }
  
  // First confirmation dialog
  const confirmed = window.confirm(
    t('auth.resetWallet.criticalWarning', {
      defaultValue: 'WARNING: You are about to reset your wallet. This will delete ALL wallet data including your accounts. This action CANNOT be undone. Please make sure you have your seed phrase backed up before proceeding.'
    })
  );
  
  if (!confirmed) return;
  
  // Second confirmation dialog
  const doubleConfirmed = window.confirm(
    t('auth.resetWallet.confirmationPrompt', {
      defaultValue: 'Are you ABSOLUTELY sure? All wallet data will be permanently deleted.'
    })
  );
  
  if (!doubleConfirmed) return;
  
  setIsResetting(true);
  
  // Create a fullscreen overlay to prevent any interaction during reset
  const resetOverlay = document.createElement('div');
  resetOverlay.style.position = 'fixed';
  resetOverlay.style.top = '0';
  resetOverlay.style.left = '0';
  resetOverlay.style.width = '100%';
  resetOverlay.style.height = '100%';
  resetOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
  resetOverlay.style.color = 'white';
  resetOverlay.style.display = 'flex';
  resetOverlay.style.flexDirection = 'column';
  resetOverlay.style.alignItems = 'center';
  resetOverlay.style.justifyContent = 'center';
  resetOverlay.style.zIndex = '10000';
  resetOverlay.style.fontSize = '18px';
  
  const spinner = document.createElement('div');
  spinner.className = 'button-spinner';
  spinner.style.width = '30px';
  spinner.style.height = '30px';
  spinner.style.border = '3px solid rgba(255, 255, 255, 0.3)';
  spinner.style.borderRadius = '50%';
  spinner.style.borderTopColor = 'white';
  spinner.style.animation = 'spin 1s linear infinite';
  spinner.style.marginBottom = '20px';
  
  const message = document.createElement('div');
  message.textContent = t('settings.resetting', { defaultValue: 'Resetting wallet...' });
  
  resetOverlay.appendChild(spinner);
  resetOverlay.appendChild(message);
  
  document.body.appendChild(resetOverlay);
  
  // Clear sensitive data before resetting
  clearSensitiveData();
  
  // BRUTE FORCE CLEARING OF LOCAL STORAGE
  try {
    console.log("INITIATING CRITICAL WALLET DATA REMOVAL");
    
    // Track the reset in sessionStorage (which is separate from localStorage)
    sessionStorage.setItem('bitdog_fresh_reset', 'true');
    
    // Explicitly target and clear the most sensitive wallet data
    const criticalKeys = [
      'bitdog_encrypted_wallets',
      'bitdog_encrypted_seed_phrase'
    ];
    
    // DIRECT APPROACH: Use multiple clearing techniques for these critical keys
    criticalKeys.forEach(key => {
      try {
        console.log(`Removing ${key} - Method 1: removeItem`);
        localStorage.removeItem(key);
        
        console.log(`Removing ${key} - Method 2: direct assignment`);
        localStorage[key] = undefined;
        
        console.log(`Removing ${key} - Method 3: delete operator`);
        delete localStorage[key];
        
        // Check if key was removed
        const stillExists = localStorage.getItem(key);
        if (stillExists) {
          console.error(`CRITICAL: Failed to remove ${key}, still present: ${stillExists.substring(0, 20)}...`);
        } else {
          console.log(`Successfully removed ${key}`);
        }
      } catch (keyError) {
        console.error(`Error removing ${key}:`, keyError);
      }
    });
    
    // SECONDARY APPROACH: Complete localStorage clearing
    console.log("Clearing entire localStorage");
    localStorage.clear();
    
    // VERIFICATION: Check if localStorage was cleared
    const remainingData = Object.keys(localStorage);
    if (remainingData.length > 0) {
      console.error("CRITICAL: Failed to clear localStorage, items remaining:", remainingData);
      
      // Last resort: Try to reset localStorage directly through indexedDB clear
      if (window.indexedDB) {
        try {
          console.log("LAST RESORT: Attempting to clear indexedDB storage");
          const req = indexedDB.deleteDatabase('localStorage');
          req.onsuccess = () => console.log("IndexedDB localStorage cleared");
          req.onerror = () => console.error("Failed to clear IndexedDB localStorage");
        } catch (idbError) {
          console.error("Error clearing indexedDB:", idbError);
        }
      }
    } else {
      console.log("Successfully cleared localStorage");
    }
  } catch (error) {
    console.error("Error during localStorage cleanup:", error);
  }
  
  // CRITICAL: Force a complete page reload with cache busting and enough delay
  // to ensure localStorage operations complete
  setTimeout(() => {
    try {
      // Do one final localStorage clear right before navigation
      localStorage.clear();
      
      // Clear all cookies as well
      document.cookie.split(';').forEach(cookie => {
        document.cookie = cookie.replace(/^ +/, '').replace(/=.*/, 
          `=;expires=${new Date(0).toUTCString()};path=/`);
      });
      
      // Force a hard reload to prevent any caching
      window.location.href = `/?reset=${Date.now()}`;
    } catch (navError) {
      console.error("Error during final navigation:", navError);
      // Force reload even if there was an error
      window.location.reload(true);
    }
  }, 1500); 
};
  
  // Toggle seed phrase section with improved handling
  const handleToggleSeedPhraseSection = async () => {
    // If we were showing the section, hide it and reset the state
    if (showSeedPhraseSection) {
      setShowSeedPhraseSection(false);
      
      // SECURITY IMPROVEMENT: Clear all sensitive data
      clearSensitiveData();
      setSeedPhraseMasked('');
      setShowSeedPhrase(false);
      setSeedPasswordVerified(false);
      setSeedPassword('');
      setSeedPasswordError('');
      setSeedCopied(false);
    } else {
      // Otherwise, show the section
      setShowSeedPhraseSection(true);
      
      try {
        // First attempt: Get directly from memory
        let phrase = getSeedPhrase();
        
        // Second attempt: If not in memory, force load from storage
        if (!phrase) {
          console.log("No seed phrase in memory, attempting to force-load");
          phrase = await forceLoadSeedPhrase();
        }
        
        // Third attempt: If still not available, try wallet decryption 
        if (!phrase && isAuthenticated) {
          console.log("Still no seed phrase, attempting wallet decryption");
          // Attempt to retry wallet decryption
          await retryWalletDecryption();
          
          // Check again after decryption
          phrase = getSeedPhrase();
          
          // Last attempt: direct force-load again
          if (!phrase) {
            console.log("Final attempt with direct loading");
            phrase = await forceLoadSeedPhrase();
          }
        }
        
        // If we have the seed phrase, set it securely
        if (phrase) {
          console.log("Seed phrase retrieved successfully");
          
          // SECURITY IMPROVEMENT: Create a secure temporary accessor instead of storing directly
          const accessor = createTemporaryAccess(phrase, 5 * 60 * 1000); // 5 minute expiry
          setSeedPhraseAccessor(accessor);
          
          // Create a masked version for display
          setSeedPhraseMasked(maskSensitiveText(phrase));
          
          // If no password is set, or password is deferred, show seed phrase directly
          if (!hasPassword || passwordDeferred) {
            console.log("No password set or password deferred, showing seed phrase directly");
            setSeedPasswordVerified(true);
            // Don't show it yet until user clicks the eye icon
            setShowSeedPhrase(false);
          } else {
            // Regular password protection - require verification
            console.log("Password protection active, verification required");
            setSeedPasswordVerified(false);
            setShowSeedPhrase(false);
          }
        } else {
          // No seed phrase found - clearly indicate this
          console.log("No seed phrase found after all attempts");
          setSeedPhraseMasked("No seed phrase available. This could happen if you imported only a private key.");
          setSeedPasswordVerified(true);
          setShowSeedPhrase(true);
        }
      } catch (error) {
        console.error("Error loading seed phrase:", error);
        setSeedPhraseMasked("Error loading seed phrase: " + error.message);
        setSeedPasswordVerified(true);
        setShowSeedPhrase(true);
      }
    }
  };
  
  // Handle verification of password for seed phrase
  const verifySeedPassword = async () => {
    if (!seedPassword) {
      setSeedPasswordError(t('errors.passwordRequired', { ns: 'common' }));
      return;
    }

    try {
      setIsVerifying(true);
      
      // Verify password first if needed
      if (hasPassword && passwordHash && passwordSalt) {
        const isValid = await verifyPassword(seedPassword, passwordHash, passwordSalt);
        if (!isValid) {
          setSeedPasswordError(t('auth.errors.incorrectPassword'));
          setIsVerifying(false);
          return;
        }
      }
      
      // SECURITY IMPROVEMENT: Securely access the seed phrase without storing it directly
      // Try all methods to get the seed phrase
      let phrase = getSeedPhrase();
      
      if (!phrase) {
        console.log("No seed phrase after password verification, attempting forced reload");
        phrase = await forceLoadSeedPhrase();
        
        if (!phrase) {
          await retryWalletDecryption();
          phrase = getSeedPhrase();
          
          if (!phrase) {
            phrase = await forceLoadSeedPhrase();
          }
        }
      }
      
      if (!phrase) {
        setSeedPasswordError('Could not retrieve seed phrase. You may have imported a private key instead of a seed phrase.');
        setIsVerifying(false);
        return;
      }
      
      // SECURITY IMPROVEMENT: Create a secure temporary accessor and masked version
      const accessor = createTemporaryAccess(phrase, 5 * 60 * 1000); // 5 minute expiry
      setSeedPhraseAccessor(accessor);
      setSeedPhraseMasked(maskSensitiveText(phrase));
      
      // Set verified
      setSeedPasswordVerified(true);
      setSeedPasswordError('');
      
      // Clear the password input
      setSeedPassword('');
      
    } catch (error) {
      console.error('Error getting seed phrase:', error);
      setSeedPasswordError('Error retrieving seed phrase: ' + error.message);
    } finally {
      setIsVerifying(false);
    }
  };
  
  // Toggle visibility of seed phrase
  const toggleSeedPhraseVisibility = () => {
    // If already showing, hide it
    if (showSeedPhrase) {
      setShowSeedPhrase(false);
      // Clear timeout
      if (seedPhraseTimeout) {
        clearTimeout(seedPhraseTimeout);
        setSeedPhraseTimeout(null);
      }
    } else {
      // If hiding, show it and set up auto-hide
      setShowSeedPhrase(true);
      
      // Set up a timeout to hide it automatically after 2 minutes
      const timer = setTimeout(() => {
        setShowSeedPhrase(false);
      }, 2 * 60 * 1000); // 2 minutes
      
      setSeedPhraseTimeout(timer);
    }
  };
  
  // Copy seed phrase to clipboard
  const copySeedPhrase = () => {
    // Only allow copying if we have a valid accessor and it's verified
    if (seedPhraseAccessor && seedPasswordVerified) {
      try {
        // Get the seed phrase securely for copying
        const seedPhrase = seedPhraseAccessor.access();
        if (seedPhrase) {
          navigator.clipboard.writeText(seedPhrase);
          setSeedCopied(true);
          setTimeout(() => setSeedCopied(false), 2000);
          
          // Show a warning about clipboard security
          setTimeout(() => {
            alert(t('settings.security.recovery.clipboardWarning'));
          }, 500);
        }
      } catch (error) {
        console.error('Error copying seed phrase:', error);
        alert('Failed to copy: ' + error.message);
      }
    }
  };
  
  // Helper function to force re-render the component
  const forceUpdate = () => {
    setRefreshKey(prev => prev + 1);
  };
  
  return (
    <div className="security-settings" key={`security-settings-${refreshKey}`}>
      <h2 className="settings-section-title">{t('settings.security.title')}</h2>
      
      {/* Password Section */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>{t('settings.security.walletPassword')}</h3>
        </div>
        <div className="settings-card-content">
          <p className="settings-description">
            {hasPassword
              ? t('settings.security.passwordProtected')
              : hasAutoPassword
              ? t('settings.security.passwordDeferred')
              : t('settings.security.passwordNeeded')}
          </p>
          
          <div className="settings-actions">
            {hasPassword ? (
              <>
                <button 
                  className="settings-button primary"
                  onClick={() => handlePasswordClick('change')}
                >
                  {t('settings.security.changePassword')}
                </button>
                <button 
                  className="settings-button secondary"
                  onClick={() => handlePasswordClick('remove')}
                  style={{ 
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '10px',
                    // Dynamic styles based on theme
                    background: document.documentElement.dataset.theme === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.07)',
                    border: document.documentElement.dataset.theme === 'dark'
                      ? '1px solid rgba(255, 255, 255, 0.2)'
                      : '1px solid rgba(0, 0, 0, 0.1)',
                    color: document.documentElement.dataset.theme === 'dark'
                      ? '#f0f0f0'
                      : '#1e293b',
                  }}
                >
                  {t('settings.security.removePassword')}
                </button>
              </>
            ) : (
              <button 
                className="settings-button primary"
                onClick={() => handlePasswordClick('create')}
              >
                {hasAutoPassword ? t('settings.security.addUnlockPassword') : t('settings.security.createPassword')}
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Seed Phrase Recovery Section */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>{t('settings.security.recovery.title')}</h3>
        </div>
        <div className="settings-card-content">
          <p className="settings-description">
            {t('settings.security.recovery.description')}
          </p>
          
          {!showSeedPhraseSection ? (
            <div className="settings-actions">
              <button 
                className="settings-button primary"
                onClick={handleToggleSeedPhraseSection}
              >
                <FiKey className="button-icon" /> {t('settings.security.recovery.viewPhrase')}
              </button>
            </div>
          ) : (
            <div className="seed-phrase-container">
              <div className="seed-warning">
                <FiAlertTriangle className="warning-icon" />
                <p>
                  {t('settings.security.recovery.warning')}
                </p>
              </div>
              
              {/* Password verification section - only show if not yet verified */}
              {!seedPasswordVerified && hasPassword && (
                <div className="seed-password-verification">
                  <h4>{t('auth.passwordModal.titles.login')}</h4>
                  <p>{t('settings.security.recovery.passwordRequired')}</p>
                  
                  <div className="seed-password-input">
                    <input
                      type="password"
                      value={seedPassword}
                      onChange={(e) => setSeedPassword(e.target.value)}
                      placeholder={t('settings.security.recovery.passwordPlaceholder')}
                      className={seedPasswordError ? 'error' : ''}
                    />
                  </div>
                  
                  <button 
                    className="settings-button primary verify-button"
                    onClick={verifySeedPassword}
                    disabled={isVerifying || !seedPassword}
                  >
                    {isVerifying ? (
                      <span className="loading-spinner-small"></span>
                    ) : (
                      t('wallet.selectorModal.verify')
                    )}
                  </button>
                  
                  {seedPasswordError && (
                    <p className="seed-password-error">{seedPasswordError}</p>
                  )}
                </div>
              )}
              
              {/* Only show seed phrase if verified */}
              {seedPasswordVerified && (
                <>
                  <div className="seed-phrase-box">
                    <div className="seed-phrase-header">
                      <h4>{t('settings.security.recovery.yourSeedPhrase')}</h4>
                      <div className="seed-actions">
                        <button 
                          className="seed-action-button"
                          onClick={toggleSeedPhraseVisibility}
                          title={showSeedPhrase ? t('actions.hide') : t('actions.show')}
                        >
                          {showSeedPhrase ? <FiEyeOff /> : <FiEye />}
                        </button>
                        
                        <button 
                          className={`seed-action-button ${seedCopied ? 'copied' : ''}`}
                          onClick={copySeedPhrase}
                          title={t('actions.copy')}
                        >
                          {seedCopied ? <FiCheck /> : <FiCopy />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="seed-phrase-value">
                      {showSeedPhrase && seedPhraseAccessor ? (
                        <div className="seed-words-grid">
                          {/* SECURITY IMPROVEMENT: Only access the seed phrase when needed for display */}
                          {(() => {
                            try {
                              const phrase = seedPhraseAccessor.access();
                              return phrase.split(' ').map((word, index) => (
                                <div key={index} className="seed-word">
                                  <span className="seed-word-number">{index + 1}.</span> {word}
                                </div>
                              ));
                            } catch (error) {
                              console.error('Error accessing seed phrase for display:', error);
                              return (
                                <div className="seed-error">
                                  Error displaying seed phrase. Please try again.
                                </div>
                              );
                            }
                          })()}
                        </div>
                      ) : (
                        <div className="seed-hidden">
                          <span>●●●● ●●●● ●●●●</span>
                          <span>{t('settings.security.recovery.eyeHint')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="seed-phrase-instructions">
                    <p>{t('settings.security.recovery.instructions')}</p>
                    {/* Add auto-hide warning */}
                    {showSeedPhrase && (
                      <p className="seed-auto-hide-warning">
                        <FiAlertTriangle /> {t('settings.security.recovery.autoHideWarning')}
                      </p>
                    )}
                  </div>
                </>
              )}
              
              <div className="settings-actions">
                <button 
                  className="settings-button secondary"
                  onClick={() => {
                    setShowSeedPhraseSection(false);
                    // SECURITY IMPROVEMENT: Clear sensitive data
                    clearSensitiveData();
                    setSeedPhraseMasked('');
                    setShowSeedPhrase(false);
                    setSeedPasswordVerified(false);
                  }}
                >
                  {t('settings.security.recovery.close')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Session Timeout Section */}
      <div className="settings-card">
        <div className="settings-card-header">
          <h3>{t('settings.security.sessionTimeout.title')}</h3>
        </div>
        <div className="settings-card-content">
          <p className="settings-description">
            {t('settings.security.sessionTimeout.description')}
          </p>
          
          <div className="settings-field">
            <label htmlFor="timeout-select">{t('settings.security.sessionTimeout.autoLock')}</label>
            <select 
              id="timeout-select" 
              value={timeoutMinutes}
              onChange={handleTimeoutChange}
              className="settings-select"
            >
              {timeoutOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      
      {/* Danger Zone Section */}
      <div className="settings-card danger-zone">
        <div className="settings-card-header danger-header">
          <h3><FiAlertTriangle /> {t('settings.security.dangerZone.title')}</h3>
        </div>
        <div className="settings-card-content">
          <p className="settings-description danger-description">
            {t('settings.security.dangerZone.description')}
          </p>
          
          <div className="danger-action">
            <div className="danger-action-info">
              <h4>{t('settings.security.dangerZone.resetWallet.title')}</h4>
              <p>{t('settings.security.dangerZone.resetWallet.description')}</p>
            </div>
            
            {!showResetConfirm ? (
              <button 
                className="settings-button danger"
                onClick={handleShowResetConfirm}
              >
                {t('settings.security.dangerZone.resetWallet.button')}
              </button>
            ) : (
              <div className="reset-confirm-container">
                <div className="reset-confirm-input-group">
                  <label htmlFor="reset-confirm">{t('settings.security.dangerZone.resetWallet.confirmationPrompt')}</label>
                  <input
                    id="reset-confirm"
                    type="text"
                    value={resetConfirmText}
                    onChange={(e) => setResetConfirmText(e.target.value)}
                    placeholder="confirm"
                    className="reset-confirm-input"
                    autoFocus
                  />
                </div>
                <button 
                  className="settings-button danger"
                  onClick={handleResetWallet}
                  disabled={isResetting || resetConfirmText.toLowerCase() !== 'confirm'}
                >
                  {isResetting ? (
                    <>
                      <div className="button-spinner"></div>
                      {t('settings.resetting')}
                    </>
                  ) : (
                    t('settings.security.dangerZone.resetWallet.confirmationButton')
                  )}
                </button>
                <button 
                  className="settings-button secondary"
                  onClick={() => setShowResetConfirm(false)}
                  disabled={isResetting}
                >
                  {t('settings.security.dangerZone.resetWallet.cancel')}
                </button>
              </div>
            )}
          </div>

          <div className="danger-action-warning">
            <FiAlertTriangle className="warning-icon" />
            <p>{t('settings.security.dangerZone.warning')}</p>
          </div>
        </div>
      </div>
      
      {/* Password Modal */}
      <PasswordModal
        isOpen={showPasswordModal}
        mode={passwordMode}
        onSuccess={handlePasswordSuccess}
        onCancel={handlePasswordCancel}
      />
    </div>
  );
};

export default SecuritySettings;